import { ISelectOption, useValidator, Validators } from '@cmctechnology/phoenix-stockbroking-web-design';
import { IValidator } from '@cmctechnology/phoenix-stockbroking-web-design/dist/js/hooks/useValidator';
import { useTranslation } from 'react-i18next';
import { createKeyValueTranslatedToOptionList } from '../../../common/mappings';
import { INCOME_SOURCE, WEALTH_SOURCE } from '../../../constants/sgp/additionalQuestions';
import { ISgpFinancialBackground } from '../../../models/sgp/profile';
import { IMultiSelectOptionOrUndefined } from '../../../models/ISelectOptionOrUndefined';

const findMultiSelectOption = (options: ISelectOption[], value: string[]) => {
  const values: IMultiSelectOptionOrUndefined = [];

  for (const option of options) {
    if (value.includes(option.value)) {
      values.push(option);
    }
  }

  return values.length > 0 ? values : undefined;
};

export const useAdditionalQuestionsValidators = (financialBackground?: ISgpFinancialBackground) => {
  const { t } = useTranslation();

  const ArrayValidator = (id: string) => Validators.custom((value: IMultiSelectOptionOrUndefined) => !!(value && value.length > 0), t('Required'), id);

  const validatorConfig = {
    validated: financialBackground?.validated,
    debounceMs: 0
  };

  const incomeSourceOptions = createKeyValueTranslatedToOptionList(INCOME_SOURCE, t);
  const wealthSourceOptions = createKeyValueTranslatedToOptionList(WEALTH_SOURCE, t);

  const hasIncomeSourceValidator = useValidator(financialBackground?.hasIncomeSource, Validators.required(t('Required'), `HasIncomeSource`), validatorConfig);

  const incomeSourceValidator = useValidator<IMultiSelectOptionOrUndefined>(
    findMultiSelectOption(incomeSourceOptions, financialBackground?.incomeSource ?? []),
    hasIncomeSourceValidator?.value ? ArrayValidator(`IncomeSourceSelect`) : Validators.custom(() => true, `IncomeSourceSelect`),
    validatorConfig
  );

  const wealthSourceValidator = useValidator<IMultiSelectOptionOrUndefined>(
    findMultiSelectOption(wealthSourceOptions, financialBackground?.wealthSource ?? []),
    ArrayValidator(`WealthSourceSelect`),
    validatorConfig
  );

  const onMultiValueChange = (validator: IValidator<IMultiSelectOptionOrUndefined>, option: ISelectOption) => {
    const opts: IMultiSelectOptionOrUndefined = validator.value ?? [];
    let newOpts = [...opts];
    if (!opts.some((x) => x?.value === option.value)) {
      newOpts.push(option);
    } else {
      newOpts = newOpts.filter((x) => x?.value !== option.value);
    }

    validator.handleEvent(newOpts, true);
  };

  return {
    hasIncomeSourceValidator,
    incomeSourceValidator,
    wealthSourceValidator,
    onMultiValueChange
  };
};
